import { supabase } from '@/integrations/supabase/client';
import { User, SubscriptionTier, UserRole, SugarDaddyProfile } from '@/types';

// Fetch user profile from Supabase
export const fetchUserProfile = async (userId: string): Promise<User | null> => {
  console.log('Auth Util: Fetching profile for ID:', userId);
  try {
    // Select specific columns including potential new profile IDs
    const { data: initialData, error } = await supabase
      .from('profiles')
      .select(`
        id,
        role,
        subscription_tier,
        is_verified,
        has_seed_phrase,
        display_name,
        created_at,
        last_login_at,
        escort_profile_id,
        sugar_baby_profile_id,
        sugar_daddy_profile_id
      `)
      .eq('id', userId)
      .maybeSingle(); 
    
    if (error) {
      // Log only unexpected errors, not the 'not found' case
      if (error.code !== 'PGRST116') { 
        console.error('Auth Util Error: Fetching profile:', error);
      }
      return null;
    }
    
    let data = initialData;
    
    if (!data) {
      console.log('Auth Util: No profile found for user ID, attempting creation:', userId);
      // Attempt to create profile if not found (e.g., first login after signup)
      // This logic might be better suited in a DB trigger after auth.users insertion
      try {
          const { data: authData } = await supabase.auth.getUser(); // Get current authenticated user
          if (authData?.user?.id === userId) { // Ensure we're creating for the right user
                const role = authData.user.user_metadata?.role || 'client';
                const displayName = authData.user.user_metadata?.displayName || authData.user.email?.split('@')[0] || 'User';
                
                const { data: newProfile, error: insertError } = await supabase
                  .from('profiles')
                  .insert({
                    id: userId,
                    role: role,
                    subscription_tier: 'free', 
                    is_verified: false, 
                    display_name: displayName,
                    // created_at defaults in DB
                    // last_login_at updated separately
                  })
                  .select()
                  .single();
                  
                if (insertError) {
                  // Handle potential race condition where profile was created between SELECT and INSERT
                  if (insertError.code === '23505') { // unique_violation
                      console.warn('Auth Util: Profile already created (race condition?), refetching...');
                      // Refetch the profile
                      const { data: refetchedData, error: refetchError } = await supabase
                            .from('profiles')
                            .select('*')
                            .eq('id', userId)
                            .single(); // Use single here, expecting it to exist now
                       if (refetchError) {
                           console.error('Auth Util Error: Refetching profile after race condition failed:', refetchError);
                           return null;
                       }
                       if (!refetchedData) return null; // Should not happen ideally
                       data = refetchedData; // Use the refetched data
                  } else {
                      console.error('Auth Util Error: Creating user profile:', insertError);
                      return null;
                  }
                } else if (newProfile) {
                     console.log('Auth Util: New profile created successfully.');
                     data = newProfile; // Use the newly created profile data
                } else {
                    console.error('Auth Util: Insert call succeeded but returned no data.');
                    return null;
                }
          } else {
               console.warn('Auth Util: User ID mismatch or no auth user found during profile creation attempt.');
               return null;
          }
      } catch (creationError) {
           console.error('Auth Util Error: Unexpected error during profile creation:', creationError);
           return null;
      }
    }
    
    // If data is available (either fetched or created), map it to User type
    if (data) {
        console.log('Auth Util: Mapping profile data to User type.', data);
        const user: User = {
          id: data.id,
          role: data.role, // Ensure this includes potential 'sugar_baby', 'sugar_daddy' from DB
          subscriptionTier: data.subscription_tier || 'free',
          isVerified: !!data.is_verified, // General ID/Age check
          hasSeedPhrase: !!data.has_seed_phrase, 
          displayName: data.display_name,
          createdAt: new Date(data.created_at),
          lastLoginAt: data.last_login_at ? new Date(data.last_login_at) : undefined,
          // Map the profile IDs
          escortProfileId: data.escort_profile_id,
          sugarBabyProfileId: data.sugar_baby_profile_id,
          sugarDaddyProfileId: data.sugar_daddy_profile_id
        };
        return user;
    } else {
        console.error('Auth Util: No profile data available after fetch/creation attempt for', userId);
        return null;
    }

  } catch (error) {
    console.error('Auth Util Error: Unexpected error in fetchUserProfile:', error);
    return null;
  }
};

// Update last login timestamp
export const updateLastLogin = async (userId: string): Promise<void> => {
  console.log('Auth Util: Updating last login for:', userId);
  try {
    const { error } = await supabase
      .from('profiles')
      .update({ last_login_at: new Date().toISOString() })
      .eq('id', userId);
    
    if (error) {
      console.error('Auth Util Error: Updating last login:', error);
    } else {
      // console.log('Auth Util: Last login timestamp updated.'); // Less verbose logging
    }
  } catch (error) {
    console.error('Auth Util Error: Unexpected error updating last login:', error);
  }
};

// Verify if a user has admin privileges
export const verifyAdminAccess = async (userId: string): Promise<boolean> => {
  console.log('Auth Util: Verifying admin access for:', userId);
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', userId)
      .maybeSingle();
      
    if (error) {
        if (error.code !== 'PGRST116') { // Ignore 'not found' 
            console.error('Auth Util Error: Verifying admin access:', error);
        }
      return false;
    }
    
    const isAdmin = data?.role === 'admin';
    console.log(`Auth Util: User ${userId} isAdmin: ${isAdmin}`);
    return isAdmin;
  } catch (error) {
    console.error('Auth Util Error: Unexpected error verifying admin access:', error);
    return false;
  }
};

// --- PERMISSION CHECKS FOR SUGAR RELATIONSHIPS --- 

/**
 * Checks if the user has the required subscription tier to browse the Sugar section.
 * Mirrors the SQL function `can_access_sugar_section` / RLS logic.
 */
export const canAccessSugarSection = (user: User | null): boolean => {
    if (!user) return false;
    return user.subscriptionTier === 'regular' || user.subscriptionTier === 'vip';
};

/**
 * Checks if a verified user (likely female role) can create a Sugar Baby profile.
 * Mirrors the SQL function `can_create_sugar_baby_profile` / RLS logic.
 */
export const canCreateSugarBabyProfile = (user: User | null): boolean => {
    if (!user) return false;
    // RLS Policy: Verified escorts only
    return user.isVerified && user.role === 'escort';
};

/**
 * Checks if a user has the required subscription to create a Sugar Daddy profile.
 * Mirrors the SQL function `can_create_sugar_daddy_profile` / RLS logic.
 */
export const canCreateSugarDaddyProfile = (user: User | null): boolean => {
    if (!user) return false;
    // RLS Policy: Regular or VIP users
    return user.subscriptionTier === 'regular' || user.subscriptionTier === 'vip';
};

/**
 * Basic check if a user can view Sugar Baby profiles (requires section access).
 * RLS handles the actual data access.
 */
export const canViewSugarBabyProfile = (viewer: User | null): boolean => {
    return canAccessSugarSection(viewer);
};

/**
 * Basic check if a user can view Sugar Daddy profiles (requires section access).
 * RLS handles the actual data access.
 */
export const canViewSugarDaddyProfile = (viewer: User | null): boolean => {
    return canAccessSugarSection(viewer);
};

/**
 * Checks if a Sugar Baby can initiate contact with a Sugar Daddy.
 * Mirrors the SQL function `can_contact_sugar_daddy`.
 * Requires the SB (viewer) to be verified and the target SD to be VIP.
 *
 * @param viewer The user attempting to initiate contact (potential SB).
 * @param targetSdUserId The User ID of the Sugar Daddy being contacted.
 * @param targetSdSubscriptionTier The Subscription Tier of the Sugar Daddy being contacted.
 */
export const canContactSugarDaddy = (
    viewer: User | null, 
    targetSdUserId: string | null | undefined, // Added target user ID
    targetSdSubscriptionTier: SubscriptionTier | null | undefined // Added target SD tier
): boolean => {
    if (!viewer || !targetSdUserId || !targetSdSubscriptionTier) return false;
    
    // Viewer must be a potential Sugar Baby (escort or SB role) and be verified.
    const isPotentialSB = viewer.role === 'sugar_baby' || viewer.role === 'escort'; 
    if (!isPotentialSB || !viewer.isVerified) {
        return false;
    }
    
    // Target SD must be VIP (as per SQL function `can_contact_sugar_daddy`)
    return targetSdSubscriptionTier === 'vip';
};

/**
 * Checks if a (verified) Sugar Daddy can contact a Sugar Baby.
 * Mirrors the SQL function `can_contact_sugar_baby`.
 * Requires the SD (viewer) to have a fully verified profile.
 *
 * @param viewer The user attempting to initiate contact (potential SD).
 * @param viewerSdProfile The profile of the potential SD (fetched separately).
 */
export const canContactSugarBaby = (viewer: User | null, viewerSdProfile: Pick<SugarDaddyProfile, 'verificationStatus'> | null): boolean => {
    // Note: We only need verificationStatus from the SD profile here.
    if (!viewer || !viewerSdProfile) return false;
    
    // Viewer must be a potential Sugar Daddy (client or SD role)
    const isPotentialSD = viewer.role === 'sugar_daddy' || viewer.role === 'client'; 
    if (!isPotentialSD) {
        return false;
    }
    
    // Viewer (SD) must have a fully verified profile (as per SQL function `can_contact_sugar_baby`)
    return viewerSdProfile.verificationStatus === 'fully_verified';
};

// --- END PERMISSION CHECKS ---
